import { normalizeAnalyticsAttributionValue } from "@/lib/analytics/attributionValue";

const FALLBACK_ORIGIN = "https://analytics.invalid";

type AnalyticsLocation = {
  url: string;
  path: string;
};

const MAX_GOOGLE_ANALYTICS_LOCATION_LENGTH = 1_000;

/**
 * Exact click ids have the highest value when the complete sanitized URL cannot fit the server's
 * page_location limit. Remaining campaign dimensions are added from most to least essential.
 */
const GA_ATTRIBUTION_QUERY_KEYS_BY_PRIORITY = [
  "gclid",
  "msclkid",
  "gbraid",
  "wbraid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

const REDACTED_PATH_SEGMENT = ":id";

/**
 * Browser routes whose literal segments are safe analytics dimensions. Route parameters are
 * always redacted by position, even when their runtime value looks like a harmless lowercase
 * route word. An unlisted path fails closed to one fully redacted sentinel; forgetting to add a
 * new route therefore reduces analytics detail without exposing a token or identifier, and an
 * arbitrarily deep path cannot exceed the page title or same-origin referrer limits.
 */
export const ANALYTICS_ROUTE_TEMPLATES = [
  "/",
  "/404",
  "/auth/google/callback",
  "/auth/google",
  "/auth/microsoft/callback",
  "/auth/discord/callback",
  "/auth/discord",
  "/auth/paypal/callback",
  "/auth/mcp/authorize",
  "/auth/mcp/complete",
  "/for-agencies",
  "/for-clipping-platforms",
  "/signin",
  "/terms-of-service",
  "/privacy-policy",
  "/blog",
  "/article/antibotting",
  "/article/brand-safety",
  "/article/future-of-clipping",
  "/invitation/accept/:token",
  "/account/document/:templateId",
  "/proof/video/callback/:proofRequestId",
  "/proof/video-stats/callback/:proofRequestId",
  "/proof/channel-stats/callback/:proofRequestId",
  "/proof/session/:token",
  "/creator/post/:platformId/:postId/start-video-proof",
  "/creator/post/:platformId/:postId/start-video-stats-proof",
  "/creator/proof/channel/:platformId/:accountId/start-channel-stats-proof",
  "/profile/added",
  "/profile",
  "/creator/payment/stripe-refresh",
  "/creator/payment",
  "/creator/prescreen/:attemptId",
  "/creator-platform/invitation/:token",
  "/creator-platform/:creatorNetworkId/campaigns/:campaignId/invitation",
  "/creator/submit",
  "/creator/campaign",
  "/creator/campaign/:token",
  "/campaign/:campaignId",
  "/campaign/:campaignId/media",
  "/campaign/:campaignId/summary",
  "/admin",
  "/admin/signin",
  "/admin/dashboard",
  "/admin/tools/users",
  "/admin/agency/team",
  "/admin/agency/tags",
  "/admin/brand",
  "/admin/brand/team",
  "/admin/tools",
  "/admin/tools/test",
  "/admin/tools/payouts",
  "/admin/tools/trace-posts",
  "/admin/tools/trace-creators",
  "/admin/tools/slashing-override",
  "/admin/tools/fraud-signal",
  "/admin/tools/slashing-review",
  "/admin/tools/botting-label-queue",
  "/admin/tools/annotate-posts",
  "/admin/tools/incident-appeals",
  "/admin/tools/channel-stats-video-proof-review",
  "/admin/tools/bots",
  "/admin/tools/manual-video-download",
  "/admin/tools/campaign-tokens",
  "/admin/tools/analytics",
  "/admin/tools/web-analytics",
  "/admin/tools/web-funnels",
  "/admin/tools/api-credentials",
  "/admin/tools/conversion-events",
  "/admin/tools/creator-platforms",
  "/admin/tools/meta-integrations",
  "/admin/tools/trace-users",
  "/admin/agency/campaigns",
  "/admin/all-campaigns",
  "/admin/network/campaigns",
  "/admin/network/channels",
  "/admin/network/profile",
  "/admin/network/team",
  "/admin/network/submit",
  "/admin/network/stats-proof",
  "/admin/agency/campaigns/create",
  "/admin/agency/campaigns/drafts/:campaignId/edit",
  "/admin/agency",
  "/admin/tools/users/:userId",
  "/admin/tools/users/:userId/rewards",
  "/admin/welcome",
  "/admin/agency/campaigns/:campaignId/detail",
  "/admin/agency/campaigns/:campaignId/report",
  "/admin/agency/campaigns/:campaignId/report/creators",
  "/admin/agency/campaigns/:campaignId/report/conversion",
  "/admin/agency/campaigns/:campaignId/report/insights",
  "/admin/agency/campaigns/:campaignId/report/posts",
  "/admin/agency/campaigns/:campaignId/report/incidents",
  "/admin/network/campaigns/:campaignId/report",
  "/admin/network/campaigns/:campaignId/report/creators",
  "/admin/network/campaigns/:campaignId/report/posts",
  "/admin/network/campaigns/:campaignId/report/incidents",
  "/admin/agency/campaigns/:campaignId/fund",
  "/admin/agency/campaigns/:campaignId/tokens",
  "/admin/agency/campaigns/:campaignId/boost",
  "/admin/agency/campaigns/:campaignId/audience",
  "/admin/agency/campaigns/:campaignId/assets",
  "/admin/agency/campaigns/:campaignId/participation",
  "/admin/agency/campaigns/:campaignId/participation/:creatorNetworkId",
  "/admin/agency/campaigns/:campaignId/fund/stripe/callback/:checkoutSessionId",
  "/admin/tools/accounts",
  "/admin/tools/accounts/:accountId",
  "/admin/agency/payouts",
  "/admin/financing",
  "/admin/financing/lenders",
  "/admin/financing/lenders/:lenderId",
  "/admin/tools/lenders",
  "/admin/tools/lenders/:lenderId",
  "/admin/agency/creators/:userId",
  "/admin/tools/creators/:userId",
  "/admin/network/creators/:networkId/:userId",
] as const;

const ANALYTICS_ROUTE_SEGMENTS = ANALYTICS_ROUTE_TEMPLATES.map(splitPath);

/**
 * Returns only the useful, non-secret parts of a browser URL.
 * `URL.pathname` excludes query parameters, fragments, and credentials.
 */
export function getAnalyticsLocation(input: string, base = FALLBACK_ORIGIN): AnalyticsLocation | undefined {
  if (!input.trim()) return undefined;

  try {
    const parsed = new URL(input, base);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return undefined;

    const path = normalizeAnalyticsRoutePath(parsed.pathname);
    return { url: `${parsed.origin}${path}`, path };
  } catch {
    return undefined;
  }
}

/** Referrer is intentionally coarser than page location: only its HTTP(S) origin is analytical. */
export function getAnalyticsOrigin(input: string, base = FALLBACK_ORIGIN): string | undefined {
  const location = getAnalyticsLocation(input, base);
  return location ? new URL(location.url).origin : undefined;
}

/**
 * Keeps a sanitized route for same-origin referrers and only the origin for external referrers.
 * This preserves useful SPA navigation lineage without exposing external paths or query values.
 */
export function getAnalyticsReferrer(input: string, siteOrigin: string): string | undefined {
  const referrer = getAnalyticsLocation(input, siteOrigin);
  const normalizedSiteOrigin = getAnalyticsOrigin(siteOrigin);
  if (!referrer || !normalizedSiteOrigin) return undefined;

  return new URL(referrer.url).origin === normalizedSiteOrigin ? referrer.url : new URL(referrer.url).origin;
}

/**
 * GA may receive only recognized campaign/click attribution query fields. Arbitrary query
 * parameters and fragments are stripped so OAuth codes, invitation tokens, filters, and other
 * application data cannot leave through page_location.
 */
export function getGoogleAnalyticsLocation(input: string, base = FALLBACK_ORIGIN): string | undefined {
  if (!input.trim()) return undefined;

  try {
    const parsed = new URL(input, base);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return undefined;

    const location = `${parsed.origin}${normalizeAnalyticsRoutePath(parsed.pathname)}`;
    if (location.length > MAX_GOOGLE_ANALYTICS_LOCATION_LENGTH) return undefined;

    const allowedEntries: string[] = [];
    let locationLength = location.length;
    for (const key of GA_ATTRIBUTION_QUERY_KEYS_BY_PRIORITY) {
      for (const value of parsed.searchParams.getAll(key)) {
        const normalized = normalizeAnalyticsAttributionValue(value);
        if (!normalized) continue;

        const encodedEntry = new URLSearchParams([[key, normalized]]).toString();
        const candidateLength = locationLength + 1 + encodedEntry.length;
        if (candidateLength <= MAX_GOOGLE_ANALYTICS_LOCATION_LENGTH) {
          allowedEntries.push(encodedEntry);
          locationLength = candidateLength;
        }
      }
    }

    return `${location}${allowedEntries.length > 0 ? `?${allowedEntries.join("&")}` : ""}`;
  } catch {
    return undefined;
  }
}

function normalizeAnalyticsRoutePath(path: string): string {
  const segments = splitPath(path);
  if (segments.length === 0) return "/";

  const routeTemplate = ANALYTICS_ROUTE_SEGMENTS.find(
    (template) =>
      template.length === segments.length &&
      template.every(
        (templateSegment, index) => templateSegment.startsWith(":") || templateSegment === segments[index],
      ),
  );

  const safeSegments = routeTemplate
    ? routeTemplate.map((segment) => (segment.startsWith(":") ? REDACTED_PATH_SEGMENT : segment))
    : [REDACTED_PATH_SEGMENT];
  return `/${safeSegments.join("/")}`;
}

function splitPath(path: string): string[] {
  return path.split("/").filter(Boolean);
}
